import cn from 'classnames'
import {
    ReactElement,
    Dispatch,
    SetStateAction,
    useEffect,
    useState
} from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
    ConfigurableProductOptions,
    ConfigurableProduct
} from '@pylot-data/pylotschema'
import { SelectedOptions } from '@corratech/pylot-variant-selector'
import { ProductPrice } from '../ProductPrice/ProductPrice'
import { useProductUI } from '@pylot-data/hooks/product/use-product-ui'
import { QuantitySelector } from '@components/QuantitySelector/QuantitySelector'
import { DisabledValues } from '@corratech/pylot-variant-selector/src/utils/getDisabledOptionValues'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next/pages'
import { Button } from '@corratech/pylot-ui'
import BottomDrawer from '../BottomDrawer'

import s from './ConfigurableAddToCart.module.scss'

export interface ConfigurableAddToCartProps {
    isAddToCartDisabled: boolean
    productUrl: string
    sizeCategoryOption: ConfigurableProductOptions | null
    disabledOptions: DisabledValues | null
    setSelectedOptions?: Dispatch<SetStateAction<SelectedOptions>>
    addProductHandler: (
        selectedOptions: SelectedOptions,
        quantity: number
    ) => void
    product: ConfigurableProduct
    /** Opens the options drawer on mount (e.g. after SRP on-demand fetch). */
    defaultOpen?: boolean
}

export const ConfigurableAddToCart = ({
    isAddToCartDisabled,
    productUrl,
    sizeCategoryOption,
    disabledOptions,
    addProductHandler,
    product,
    defaultOpen = false
}: ConfigurableAddToCartProps): ReactElement | null => {
    const [isBottomDrawerOpen, setIsBottomDrawerOpen] =
        useState<boolean>(defaultOpen)
    const { t } = useTranslation(['pdp', 'common', 'wishlist'])
    const router = useRouter()

    useEffect(() => {
        if (defaultOpen) {
            setIsBottomDrawerOpen(true)
        }
    }, [defaultOpen])

    const {
        setSelectedOptions,
        selectedOptions,
        variant,
        quantity,
        setQuantity
    } = useProductUI(product)

    const { price_range: variantPriceRange } = variant
    const { values } = sizeCategoryOption || {}

    const sizeValuesList =
        values &&
        values?.length > 0 &&
        values.map((switchOption, index) => {
            const isSelected = switchOption?.label === selectedOptions?.talla
            const isDisabled = disabledOptions?.talla.includes(
                switchOption?.label!
            )

            return (
                <motion.div
                    animate={{ scale: isSelected ? 1 : 0.99 }}
                    transition={{ duration: 0.3 }}
                    className={cn(s.sizeSelector, {
                        [s.sizeDisabled]: isDisabled
                    })}
                    key={index}
                >
                    <div
                        role="button"
                        tabIndex={isDisabled ? -1 : 0}
                        className={s.size}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !isDisabled) {
                                setSelectedOptions((prevState) => {
                                    const newSelectedOptions = {
                                        ...prevState,
                                        talla: switchOption?.swatch_data?.value!
                                    }
                                    return newSelectedOptions
                                })
                            }
                        }}
                        onClick={() => {
                            if (isDisabled) return
                            setSelectedOptions((prevState) => {
                                const newSelectedOptions = {
                                    ...prevState,
                                    talla: switchOption?.swatch_data?.value!
                                }
                                setQuantity(1)
                                return newSelectedOptions
                            })
                        }}
                        key={switchOption?.uid}
                    >
                        <span
                            className={cn({
                                [s.sizeSelected]: isSelected,
                                [s.size]: !isSelected
                            })}
                        >
                            {switchOption?.label}
                        </span>
                    </div>
                    {isSelected && (
                        <div className={s.quantity}>
                            <QuantitySelector
                                quantity={quantity}
                                setQuantity={setQuantity}
                            />
                        </div>
                    )}
                    {isSelected && (
                        <div className={s.price}>
                            <ProductPrice
                                className={String(
                                    product.__typename
                                ).toLowerCase()}
                                priceRange={variantPriceRange}
                                hideDiscountPercent
                                quantity={quantity}
                            />
                        </div>
                    )}
                </motion.div>
            )
        })

    const drawerButton = (
        <Button
            onClick={() => setIsBottomDrawerOpen(true)}
            disabled={isAddToCartDisabled}
            className={cn('btn-add-to-cart-plp-pdp', s['drawerButton'])}
        >
            <h5>
                {isAddToCartDisabled
                    ? t('common||Out of Stock')
                    : t('common||pdp|Choose options')}
            </h5>
        </Button>
    )

    const handleDrawerClose = () => {
        setIsBottomDrawerOpen(false)
    }

    const handleGoToProduct = () => {
        handleDrawerClose()
        router.push(productUrl)
    }

    const handleAddToCart = () => {
        addProductHandler(selectedOptions, quantity)
        handleDrawerClose()
    }

    return (
        <>
            {drawerButton}
            <AnimatePresence
                initial={false}
                mode="wait"
                onExitComplete={() => null}
            >
                {isBottomDrawerOpen && (
                    <BottomDrawer onClose={handleDrawerClose}>
                        <div className={s.root}>
                            <div className={s.titleContainer}>
                                <div className={s.title}>
                                    <div>{t('common||pdp|Select size')}</div>
                                </div>
                                <button
                                    onClick={() => handleGoToProduct()}
                                    className={s.details}
                                >
                                    <p>{t('common||pdp|View details')}</p>
                                </button>
                            </div>
                            <div className={s.attrContainer}>
                                {sizeValuesList}
                            </div>
                            <Button
                                onClick={() => handleAddToCart()}
                                disabled={isAddToCartDisabled}
                                className={cn(
                                    'btn-add-to-cart-plp-pdp',
                                    s['addToCart']
                                )}
                            >
                                <h5>
                                    {isAddToCartDisabled
                                        ? t('common||Out of Stock')
                                        : t('common||pdp|Quick add')}
                                </h5>
                            </Button>
                        </div>
                    </BottomDrawer>
                )}
            </AnimatePresence>
        </>
    )
}

export default ConfigurableAddToCart
